/**
 * GDPR Consent Component
 * Shows GDPR consent modal for EU users
 */

import { createLogger } from '../core/logger';
import { getState, setGDPRConsent } from '../core/state';
import { GDPRConsent } from '../types';

const logger = createLogger('GDPRConsent');

const GDPR_STORAGE_KEY = 'gdprConsent';

/**
 * Check if GDPR consent modal should be shown
 */
export function shouldShowGDPRConsent(showGDPRConsent: boolean): boolean {
  if (!showGDPRConsent) return false;

  // Check if already consented in state
  if (getState().compliance.gdprConsent) return false;

  // Check localStorage for previous consent
  try {
    const stored = localStorage.getItem(GDPR_STORAGE_KEY);
    if (stored) {
      const consent = JSON.parse(stored) as GDPRConsent;
      setGDPRConsent(consent);
      return false;
    }
  } catch {
    // Storage unavailable or invalid data
  }

  return true;
}

/**
 * Show the GDPR consent modal
 * Returns a promise that resolves with the consent choice
 */
export function showGDPRConsentModal(): Promise<GDPRConsent> {
  return new Promise((resolve) => {
    logger.info('Showing GDPR consent modal');

    // Create overlay
    const overlay = document.createElement('div');
    overlay.id = 'gdpr-overlay';
    overlay.className = 'modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'gdpr-title');

    overlay.innerHTML = `
      <div class="modal gdpr-modal">
        <div class="modal-header">
          <div class="modal-icon">🍪</div>
          <h2 class="modal-title" id="gdpr-title">Privacy & Cookies</h2>
          <p class="modal-subtitle">We value your privacy</p>
        </div>

        <div class="modal-body">
          <p>We use cookies and similar technologies to enhance your experience, analyze site usage, and assist in our marketing efforts.</p>
          <p>By clicking "Accept All", you consent to the use of ALL cookies. You may also choose "Essential Only" to allow only necessary cookies.</p>
          <details class="gdpr-details">
            <summary>Learn more about our cookies</summary>
            <div class="gdpr-details-content">
              <p><strong>Essential Cookies:</strong> Required for the site to function properly. Cannot be disabled.</p>
              <p><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our site.</p>
              <p><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements and track campaign effectiveness.</p>
            </div>
          </details>
        </div>

        <div class="modal-footer gdpr-buttons">
          <button type="button" class="btn btn-secondary" id="gdpr-essential">
            Essential Only
          </button>
          <button type="button" class="btn btn-primary" id="gdpr-accept">
            Accept All
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Animate in
    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
    });

    const handleConsent = (consented: boolean, type: 'marketing' | 'necessary') => {
      const consent: GDPRConsent = {
        consented,
        timestamp: new Date().toISOString(),
        type
      };

      logger.info('GDPR consent received', { consented, type });
      setGDPRConsent(consent);

      // Store in localStorage (persists across sessions)
      try {
        localStorage.setItem(GDPR_STORAGE_KEY, JSON.stringify(consent));
      } catch {
        // Storage unavailable
      }

      // Close modal
      overlay.classList.remove('is-visible');
      setTimeout(() => overlay.remove(), 200);

      resolve(consent);
    };

    // Handle Accept All
    document.getElementById('gdpr-accept')?.addEventListener('click', () => {
      handleConsent(true, 'marketing');
    });

    // Handle Essential Only
    document.getElementById('gdpr-essential')?.addEventListener('click', () => {
      handleConsent(false, 'necessary');
    });

    // Focus the Accept button
    setTimeout(() => {
      document.getElementById('gdpr-accept')?.focus();
    }, 100);
  });
}
